import { useCallback } from "react";
import {
  normalizeVorlageForSave,
  removeVorlageById,
  updateVorlageById,
  validateEditableVorlageInput,
} from "../utils/vorlagenCrudHelpers";

export default function useVorlagenOperations({
  isAdmin,
  vorlagen,
  setVorlagen,
  persistUserVorlagen,
  upsertUserPromptInTable,
  deleteUserPromptInTable,
  vorlagenService,
  tableNames,
  loadAllVorlagen,
  showStatus,
  formatSupabaseError,
}) {
  const deletePromptById = useCallback(async (promptId) => {
    if (!promptId) return false;

    if (!isAdmin) {
      const nextVorlagen = removeVorlageById(vorlagen, promptId);

      const userPromptDelete = await deleteUserPromptInTable(promptId);
      if (!userPromptDelete.ok && userPromptDelete.supported) {
        showStatus(`Fehler beim Löschen: ${formatSupabaseError(userPromptDelete.error)}`, true);
        return false;
      }

      setVorlagen(nextVorlagen);
      await persistUserVorlagen(nextVorlagen);
      showStatus("Vorlage gelöscht ✓");
      return true;
    }

    try {
      const { error } = await vorlagenService.deletePromptById(tableNames, promptId);
      if (error) throw error;
      await loadAllVorlagen();
      showStatus("Vorlage gelöscht ✓");
      return true;
    } catch (err) {
      showStatus(`Fehler beim Löschen: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [deleteUserPromptInTable, formatSupabaseError, isAdmin, loadAllVorlagen, persistUserVorlagen, setVorlagen, showStatus, tableNames, vorlagen, vorlagenService]);

  const saveVorlageInline = useCallback(async (prompt) => {
    const validationError = validateEditableVorlageInput(prompt);
    if (validationError) {
      showStatus(validationError, true);
      return false;
    }

    const normalizedPrompt = normalizeVorlageForSave(prompt);

    if (!isAdmin) {
      const userPromptUpdate = await upsertUserPromptInTable(normalizedPrompt);
      if (!userPromptUpdate.ok && userPromptUpdate.supported) {
        showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(userPromptUpdate.error)}`, true);
        return false;
      }

      const nextVorlagen = updateVorlageById(vorlagen, normalizedPrompt);
      setVorlagen(nextVorlagen);
      await persistUserVorlagen(nextVorlagen);
      showStatus("Vorlage aktualisiert ✓");
      return true;
    }

    try {
      const { error } = await vorlagenService.upsertPrompt(tableNames, {
        id: normalizedPrompt.id,
        name: normalizedPrompt.name,
        mod: normalizedPrompt.mod,
        befund: normalizedPrompt.befund,
        beurteilung: normalizedPrompt.beurteilung,
      });
      if (error) throw error;
      await loadAllVorlagen();
      showStatus("Vorlage aktualisiert ✓");
      return true;
    } catch (err) {
      showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [formatSupabaseError, isAdmin, loadAllVorlagen, persistUserVorlagen, setVorlagen, showStatus, tableNames, upsertUserPromptInTable, vorlagen, vorlagenService]);

  const renameVorlagenSubsection = useCallback(async (vorlagenEntries, nextSubsection) => {
    const entries = Array.isArray(vorlagenEntries) ? vorlagenEntries.filter((entry) => entry?.id) : [];
    const normalizedSubsection = String(nextSubsection || "").trim();

    if (!normalizedSubsection) {
      showStatus("Bitte Untersektion eingeben.", true);
      return false;
    }

    if (entries.length === 0) {
      showStatus("Keine Vorlagen in dieser Untersektion gefunden.", true);
      return false;
    }

    const normalizedEntries = entries.map((entry) => normalizeVorlageForSave({
      ...entry,
      subsection: normalizedSubsection,
    }));

    if (!isAdmin) {
      const results = await Promise.all(normalizedEntries.map((entry) => upsertUserPromptInTable(entry)));
      const failed = results.find((result) => !result.ok && result.supported);
      if (failed) {
        showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(failed.error)}`, true);
        return false;
      }

      const byId = new Map(normalizedEntries.map((entry) => [entry.id, entry]));
      const nextVorlagen = vorlagen.map((entry) => byId.get(entry.id) || entry);

      setVorlagen(nextVorlagen);
      await persistUserVorlagen(nextVorlagen);
      showStatus("Untersektion aktualisiert ✓");
      return true;
    }

    try {
      const results = await Promise.all(normalizedEntries.map((entry) => (
        vorlagenService.upsertPrompt(tableNames, {
          id: entry.id,
          name: entry.name,
          mod: entry.mod,
          befund: entry.befund,
          beurteilung: entry.beurteilung,
        })
      )));
      const failed = results.find((result) => result.error);
      if (failed?.error) throw failed.error;
      await loadAllVorlagen();
      showStatus("Untersektion aktualisiert ✓");
      return true;
    } catch (err) {
      showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [formatSupabaseError, isAdmin, loadAllVorlagen, persistUserVorlagen, setVorlagen, showStatus, tableNames, upsertUserPromptInTable, vorlagen, vorlagenService]);

  const deleteVorlagenSubsection = useCallback(async (vorlagenEntries) => {
    const entries = Array.isArray(vorlagenEntries) ? vorlagenEntries.filter((entry) => entry?.id) : [];

    if (entries.length === 0) {
      showStatus("Leere Untersektion entfernt ✓");
      return true;
    }

    if (!isAdmin) {
      const results = await Promise.all(entries.map((entry) => deleteUserPromptInTable(entry.id)));
      const failed = results.find((result) => !result.ok && result.supported);
      if (failed) {
        showStatus(`Fehler beim Löschen: ${formatSupabaseError(failed.error)}`, true);
        return false;
      }

      const ids = new Set(entries.map((entry) => entry.id));
      const nextVorlagen = vorlagen.filter((entry) => !ids.has(entry.id));

      setVorlagen(nextVorlagen);
      await persistUserVorlagen(nextVorlagen);
      showStatus("Untersektion gelöscht ✓");
      return true;
    }

    try {
      const results = await Promise.all(entries.map((entry) => vorlagenService.deletePromptById(tableNames, entry.id)));
      const failed = results.find((result) => result.error);
      if (failed?.error) throw failed.error;
      await loadAllVorlagen();
      showStatus("Untersektion gelöscht ✓");
      return true;
    } catch (err) {
      showStatus(`Fehler beim Löschen: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [deleteUserPromptInTable, formatSupabaseError, isAdmin, loadAllVorlagen, persistUserVorlagen, setVorlagen, showStatus, tableNames, vorlagen, vorlagenService]);

  return {
    deletePromptById,
    saveVorlageInline,
    renameVorlagenSubsection,
    deleteVorlagenSubsection,
  };
}
